import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import PageHeader from './components/PageHeader';
import { LabelEditor } from './wysiwyg/LabelEditor';
import type { Template as WysiwygTemplate, OutputFormat } from './wysiwyg/types';
import { getFormatOrDefault } from './wysiwyg/formats/index';
import { convertTemplateDpi } from './wysiwyg/dpiConversion';
import { createLegacyAssetResolver } from './wysiwyg/legacyAssets';
import { normalizeLoadedTemplate } from './wysiwyg/templateNormalization';
import { AUTH_STORAGE_KEY, DEFAULT_API_BASE_URL } from './constants';
import type { BrandRecord, MarketRecord } from './types';
import NewTemplateChooserModal, { type TemplateFormatSelection, type TemplateProvenance } from './NewTemplateChooserModal';

function buildDefaultTemplate(format: OutputFormat = 'pdf', dpi: number = 72): WysiwygTemplate {
  const formatDef = getFormatOrDefault(format);
  const realWidth = 4;
  const realHeight = 6;
  return {
    id: 'new-template',
    name: 'Untitled Label',
    organization: '',
    outputFormat: format,
    dpi,
    width: Math.round(realWidth * dpi),
    height: Math.round(realHeight * dpi),
    realWidth,
    realHeight,
    realUnit: 'in',
    background: formatDef.templateDefaults.background ?? '#f7f8fa',
    elements: [],
    themeVariables: [],
  };
}

type NewTemplatePageProps = {
  onClose: () => void;
  onSave: (template: WysiwygTemplate, marketIds: number[], brandIds: number[]) => void;
  organizationId?: string;
  userId?: number | string;
  marketIds?: number[];
  availableMarkets?: MarketRecord[];
  brandIds?: number[];
  availableBrands?: BrandRecord[];
};

export default function NewTemplatePage({
  onClose,
  onSave,
  organizationId,
  userId,
  marketIds,
  availableMarkets,
  brandIds,
  availableBrands,
}: NewTemplatePageProps) {
  const location = useLocation();
  const [startingPoint, setStartingPoint] = useState<{
    sourceTemplate?: WysiwygTemplate;
    templateName?: string;
    format?: OutputFormat;
    dpi?: number;
    marketIds?: number[];
    brandIds?: number[];
    provenance?: TemplateProvenance;
  }>(location.state ?? {});
  const [showChooser, setShowChooser] = useState(false);

  const initialTemplate = useMemo<WysiwygTemplate>(() => {
    const source = startingPoint.sourceTemplate;
    const templateName = startingPoint.templateName;
    const format = startingPoint.format ?? 'pdf';
    const dpi = startingPoint.dpi ?? 72;

    if (source) {
      const converted = convertTemplateDpi(normalizeLoadedTemplate(source), dpi, format);
      return {
        ...converted,
        id: 'new-template',
        name: templateName || `${source.name} - Clone`,
      };
    }
    return buildDefaultTemplate(format, dpi);
  }, [startingPoint]);

  const isNewBlank = !startingPoint.sourceTemplate;
  const pageTitle = isNewBlank ? 'New Template' : `New Template: ${initialTemplate.name}`;
  const initialMarketIds = startingPoint.marketIds ?? marketIds;
  const initialBrandIds = initialTemplate.allBrands
    ? brandIds
    : startingPoint.brandIds ?? initialTemplate.brandIds ?? brandIds;

  const handleChangeStartingPoint = (hasChanges: boolean) => {
    if (hasChanges && !window.confirm('Discard your changes and choose a different starting point?')) return;
    setShowChooser(true);
  };

  const chooseBlank = (formatSelection: TemplateFormatSelection) => {
    setStartingPoint({ format: formatSelection.format, dpi: formatSelection.dpi });
    setShowChooser(false);
  };

  const handleSave = (template: WysiwygTemplate, nextMarketIds: number[], nextBrandIds: number[]) => {
    onSave(template, nextMarketIds, nextBrandIds);
  };

  const legacyAssetResolver = useMemo(() => {
    if (!organizationId) {
      return undefined;
    }
    return createLegacyAssetResolver({
      organizationId,
      apiBaseUrl: DEFAULT_API_BASE_URL,
      getToken: () => window.localStorage.getItem(AUTH_STORAGE_KEY),
    });
  }, [organizationId]);

  return (
    <div className="stack" data-testid="new-template-page">
      <PageHeader
        title={pageTitle}
        subtitle="Templates"
        actions={(
          <button className="button secondary" onClick={onClose} data-testid="new-template-close">
            Back to Templates
          </button>
        )}
      />

      {!showChooser && <div style={{ marginTop: -12 }}>
        <LabelEditor
          initialTemplate={initialTemplate}
          onSave={handleSave}
          legacyAssetResolver={legacyAssetResolver}
          organizationId={organizationId ? Number(organizationId) : undefined}
          marketIds={initialMarketIds}
          availableMarkets={availableMarkets}
          brandIds={initialBrandIds}
          availableBrands={availableBrands}
          provenance={startingPoint.provenance}
          onChangeStartingPoint={handleChangeStartingPoint}
        />
      </div>}
      {showChooser && (
        <NewTemplateChooserModal
          organizationId={organizationId ?? null}
          userId={userId}
          initialFormat={{ format: initialTemplate.outputFormat ?? 'pdf', dpi: initialTemplate.dpi ?? 72 }}
          onSelectBlank={chooseBlank}
          onSelectTemplate={(sourceTemplate, templateName, formatSelection, nextMarketIds, provenance) => {
            setStartingPoint({ sourceTemplate, templateName, ...formatSelection, marketIds: nextMarketIds, provenance });
            setShowChooser(false);
          }}
          onClose={() => setShowChooser(false)}
        />
      )}
    </div>
  );
}
